import { Parse } from '@cadstrom/cadstrom-shared';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import Slider from '@mui/material/Slider';
import { useId, useState } from 'react';

import { MIN_DISTANCE_MM } from '../status-bar/grid-utils.js';

import type { ModelGrid } from '@cadstrom/sigma-engine-shared';
import type { SelectChangeEvent } from '@mui/material/Select';
import type { CadRendererGridSettings, CadRendererGridType } from '../sync/bridges/grid-bridge.js';

export interface GridSettingsFormProps {
	/** Current grid settings to show. */
	settings: CadRendererGridSettings;
	/** The settings Reset returns to. */
	defaults: CadRendererGridSettings;
	/** Whether the renderer can draw a grid; without it only distance and units are offered. */
	isGridSupported: boolean;
	/** Whether to offer the spacing and alpha sliders. */
	showAppearanceSliders: boolean;
	/** Receives each change as a partial update. */
	onChange: (settings: Partial<CadRendererGridSettings>) => void;
}

/**
 * Edits grid distance, units, and style, with Reset back to the given defaults. A distance being
 * typed is only reported once it parses as a number, clamped to the smallest supported distance;
 * leaving the field shows the reported distance again.
 *
 * @param props - See {@link GridSettingsFormProps}.
 * @returns The form fields and Reset button.
 */
export function GridSettingsForm({
	settings,
	defaults,
	isGridSupported,
	showAppearanceSliders,
	onChange,
}: GridSettingsFormProps) {
	const id = useId();
	const { distance, units, gridType, spacingPercent, alphaPercent } = settings;
	const [distanceDraft, setDistanceDraft] = useState<string | null>(null);
	const [distanceInputIsError, setDistanceInputIsError] = useState(false);
	const distanceDraftValue = distanceDraft ?? String(distance);

	const minDistance = Parse.Util.convertGridDistanceUnits(MIN_DISTANCE_MM, 'mm', units);

	/**
	 * Keeps what is typed as a draft and reports it as the distance once it is a number, clamped to
	 * the smallest supported distance; anything else marks the field invalid and reports nothing.
	 *
	 * @param event - The distance field's change event.
	 */
	const handleDistanceChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
		setDistanceDraft(event.target.value);

		const value = event.target.value.trim() === '' ? Number.NaN : Number(event.target.value);
		if (Number.isFinite(value)) {
			onChange({ distance: Math.max(minDistance, value) });
			setDistanceInputIsError(false);
		} else {
			setDistanceInputIsError(true);
		}
	};

	/**
	 * Switches units and reports the current distance converted into them, clamped to the smallest
	 * supported distance, discarding any draft.
	 *
	 * @param event - The units select's change event.
	 */
	const handleUnitsChange = (event: SelectChangeEvent<ModelGrid['units']>): void => {
		const newUnits = event.target.value;
		const newMin = Parse.Util.convertGridDistanceUnits(MIN_DISTANCE_MM, 'mm', newUnits);
		const converted = parseFloat(Parse.Util.convertGridDistanceUnits(distance, units, newUnits).toPrecision(11));
		const newDistance = Number.isFinite(converted) ? Math.max(newMin, converted) : converted;
		setDistanceDraft(null);
		setDistanceInputIsError(false);
		onChange({ distance: newDistance, units: newUnits });
	};

	/**
	 * Reports a newly chosen grid style along with the spacing that suits it.
	 *
	 * @param event - The style select's change event.
	 */
	const handleGridTypeChange = (event: SelectChangeEvent<CadRendererGridType>) => {
		if (event.target.value === gridType) {
			return;
		}

		onChange({
			gridType: event.target.value as CadRendererGridType,
			spacingPercent: event.target.value === 'dash' ? 0.3 : 1.0,
		});
	};

	const isResettable =
		distance !== defaults.distance ||
		units !== defaults.units ||
		gridType !== defaults.gridType ||
		spacingPercent !== defaults.spacingPercent ||
		alphaPercent !== defaults.alphaPercent;

	/** Discards any draft and reports the defaults as the new settings. */
	function resetGrid() {
		setDistanceDraft(null);
		setDistanceInputIsError(false);
		onChange(defaults);
	}

	return (
		<>
			<div className='grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-2'>
				<label htmlFor={`${id}-distance`}>Distance</label>
				<OutlinedInput
					id={`${id}-distance`}
					type='number'
					error={distanceInputIsError}
					value={distanceDraftValue}
					onChange={handleDistanceChange}
					onBlur={() => {
						setDistanceDraft(null);
						setDistanceInputIsError(false);
					}}
					inputMode='decimal'
					inputProps={{
						min: 0,
						step: 0.1,
					}}
				/>

				<label id={`${id}-units-label`} htmlFor={`${id}-units`}>
					Units
				</label>
				<Select<ModelGrid['units']>
					id={`${id}-units`}
					labelId={`${id}-units-label`}
					value={units}
					onChange={handleUnitsChange}
				>
					{Parse.Util.ValidGridUnits.map((u) => (
						<MenuItem key={u} value={u}>
							{u}
						</MenuItem>
					))}
				</Select>

				{isGridSupported && (
					<>
						<label id={`${id}-style-label`} htmlFor={`${id}-style`}>
							Style
						</label>
						<Select<CadRendererGridType>
							id={`${id}-style`}
							labelId={`${id}-style-label`}
							value={gridType}
							onChange={handleGridTypeChange}
						>
							<MenuItem value='line'>
								<span>Lines</span>
							</MenuItem>
							<MenuItem value='dot'>
								<span>Dots</span>
							</MenuItem>
							<MenuItem value='dash'>
								<span>Dashes</span>
							</MenuItem>
							<MenuItem value='plus'>
								<span>Crosses</span>
							</MenuItem>
							<MenuItem value='none'>
								<span>None</span>
							</MenuItem>
						</Select>

						{showAppearanceSliders && (
							<>
								<label id={`${id}-spacing-label`} htmlFor={`${id}-spacing`}>
									Spacing
								</label>
								<Slider
									id={`${id}-spacing`}
									value={spacingPercent}
									onChange={(e, newVal) =>
										onChange({
											spacingPercent: commitSliderValue(newVal),
										})
									}
									min={0.01}
									max={10}
									aria-labelledby={`${id}-spacing-label`}
									step={0.1}
									disabled={gridType === 'none' || gridType === 'line'}
									title={String(spacingPercent)}
								/>

								<label id={`${id}-alpha-percent-label`} htmlFor={`${id}-alpha-percent`}>
									Alpha percent
								</label>
								<Slider
									id={`${id}-alpha-percent`}
									value={alphaPercent}
									onChange={(e, newVal) =>
										onChange({
											alphaPercent: commitSliderValue(newVal),
										})
									}
									min={0.01}
									max={1}
									aria-labelledby={`${id}-alpha-percent-label`}
									step={0.05}
									title={String(alphaPercent)}
								/>
							</>
						)}
					</>
				)}
			</div>

			<div className='flex justify-end'>
				<Button variant='text' disabled={!isResettable} onClick={() => resetGrid()}>
					<p>Reset</p>
				</Button>
			</div>
		</>
	);
}

/**
 * Reduces a slider change to one value; range sliders report an array.
 *
 * @param value - The slider's reported value.
 * @returns The single value to store.
 */
function commitSliderValue(value: number | number[]): number {
	return Array.isArray(value) ? (value[0] ?? 0) : value;
}
